import { Component, OnInit, inject } from '@angular/core';
import {AppState} from '../store';
import {select, Store} from '@ngrx/store';
import {ThirdPartyScript} from '../models/third-party-script.model';
import {selectEmailPopupScript, selectThirdPartyScripts} from '../store/selectors';
import {filter, first, tap} from 'rxjs/operators';
import { appendEmbeddedHtml } from '../common/embedded-html-writer';

/**
 *
 * Injects third-party scripts in the DOM. Each third-party plugin can have multiple script tags.
 *
 * All scripts are injected in the DOM using innerHTML with a custom HTML id, this is just for HTML parsing purposes.
 *
 * Note that in-line scripts injected in the DOM using innerHTML are NOT loaded, and src scripts are not fetched.
 *
 * We can then query the parsed scripts from the DOM, and activate them:
 *
 * - in-line scripts are activated by running eval() on them
 *
 * - src scripts are loaded programmatically form the src address
 *
 */

@Component({
    selector: "third-party-scripts-injector",
    templateUrl: "third-party-scripts-injector.component.html",
    styleUrls: [
        "third-party-scripts-injector.component.scss"
    ],
    standalone: true
})
export class ThirdPartyScriptsInjectorComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

  ngOnInit() {
    // apply the email marketing popup, if any
    this.store
      .pipe(
        select(selectEmailPopupScript),
        first(),
        filter(script => !!script),
        tap(script => this.injectScripts([script]))
      )
      .subscribe();
    // apply other third-party scripts
    this.store
      .pipe(
        select(selectThirdPartyScripts),
        first(),
        filter(scripts => !!scripts),
        tap(scripts => this.injectScripts(scripts))
      )
      .subscribe();
  }

  injectScripts(scripts: ThirdPartyScript[]) {
    const enabledScripts = scripts.filter(script => script.enabled);
    for (const scriptConfig of enabledScripts) {
      try {
        // Deliberately NOT postscribe. postscribe funnels every caller in the app
        // through one module-level queue and only releases it once each <script> it
        // wrote reports load/error. Tracking snippets that create their own deferred
        // <script> never report, so the queue stayed wedged for the rest of the page:
        // the remaining tenant scripts here never got injected, and every unrelated
        // embed (rich-text HTML embeds, HTML code sections, third-party video
        // players) silently rendered nothing.
        appendEmbeddedHtml(document.head, scriptConfig.code).catch(err =>
          console.error(`Error injecting script ${scriptConfig.name}`, err)
        );
      } catch (err) {
        console.error(`Error injecting script ${scriptConfig.name}`, err);
      }
    }
  }

}
